/**
 * De pagina en de bijlagen (R-104, U25, R-199).
 *
 * Een bestand van de invuller gaat als bytes naar de pod (`POST /bijlagen`), die het na zijn eigen toets
 * aanlevert bij de opslag. Wat terugkomt is de verwijzing die in de stand komt, en de feiten van de opslag
 * (soort en grootte) voor de regel `bestandseisen`. Voor een bestand dat al in de stand staat, haalt de
 * pagina die feiten op (`GET /bijlagen`), zodat de browser rekent zoals de indienpoort.
 *
 * Het content-type is `application/octet-stream`, wat de browser ook van het bestand denkt: de pod leidt
 * het type af uit de naam en de opslag toetst de inhoud.
 */

import type { FileFacts } from '@eformulieren/engine'
import type { UploadOutcome } from '@eformulieren/renderer'
import { z } from 'zod'
import { CLIENT_HEADER } from './podRequests.js'
import { codeFromBody } from './rejection.js'

const Delivered = z.object({ hash: z.string(), bestandsnaam: z.string(), grootteBytes: z.number().int().nonnegative(), soort: z.string() })
const Facts = z.object({ bijlagen: z.array(z.object({ hash: z.string(), soort: z.string(), grootteBytes: z.number().int().nonnegative() })) })

export async function uploadFile(
  input: { readonly instantieId: string; readonly veld: string; readonly file: File; readonly headers: Readonly<Record<string, string>> },
  get: typeof fetch = fetch,
): Promise<{ readonly uitkomst: UploadOutcome; readonly feiten?: FileFacts }> {
  const url = `/bijlagen?instantie=${encodeURIComponent(input.instantieId)}&veld=${encodeURIComponent(input.veld)}&naam=${encodeURIComponent(input.file.name)}`
  let response: Response
  try {
    response = await get(url, {
      method: 'POST',
      headers: { ...input.headers, ...CLIENT_HEADER, 'content-type': 'application/octet-stream' },
      body: input.file,
    })
  } catch {
    return { uitkomst: { weigering: 'geen-verbinding' } }
  }
  const text = await response.text()
  if (response.status !== 201) return { uitkomst: { weigering: codeFromBody(text) ?? 'onbekend' } }
  const parsed = Delivered.safeParse(JSON.parse(text) as unknown)
  if (!parsed.success) return { uitkomst: { weigering: 'onbekend' } }
  const { hash, bestandsnaam, grootteBytes, soort } = parsed.data
  return { uitkomst: { verwijzing: { hash, bestandsnaam, grootteBytes } }, feiten: { soort, grootteBytes } }
}

export async function fetchFacts(
  input: { readonly instantieId: string; readonly hashes: readonly string[]; readonly headers: Readonly<Record<string, string>> },
  get: typeof fetch = fetch,
): Promise<ReadonlyMap<string, FileFacts>> {
  const url = `/bijlagen?instantie=${encodeURIComponent(input.instantieId)}${input.hashes.map((hash) => `&hash=${encodeURIComponent(hash)}`).join('')}`
  try {
    const response = await get(url, { headers: { ...input.headers } })
    if (response.status !== 200) return new Map()
    const parsed = Facts.safeParse(await response.json())
    if (!parsed.success) return new Map()
    return new Map(parsed.data.bijlagen.map((file) => [file.hash, { soort: file.soort, grootteBytes: file.grootteBytes }]))
  } catch {
    return new Map()
  }
}
